/**
 * Constants and types for text layers.
 *
 * Holds everything that describes a text layer: alignment, font style,
 * wrap/auto-fit/auto-width modes, the Fabric.js property keys a text
 * layer maps to, default style presets, and the currency-format options
 * used when a text layer displays a formatted number.
 *
 * Used by the text panel UI, the canvas services that build Fabric text
 * objects, and the currency formatter helpers.
 *
 * Mental model: this is the one place to check before adding a new text
 * property or currency option - it is the source of truth for "what a
 * text layer can look like".
 */
import { BASE_FABRIC_KEYS } from '@/constants/layers';
import { ICONS } from '@/constants/icons';
import type { TextLayer } from '@/types/layers/layer';
import type { CurrencyDef, SeparatorDef, SeparatorKey, TextStylePreset } from '@/types/layers/text';

export const TEXT_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right'
} as const;

export const FONT_STYLES = {
  NORMAL: 'normal',
  ITALIC: 'italic'
} as const;

export const TEXT_MODE = {
  WRAP: 'wrap',
  AUTO_FIT: 'autoFit',
  AUTO_WIDTH: 'autoWidth'
} as const;

export const TEXT_PROPS = {
  TEXT: 'text',
  WIDTH: 'width',
  FONT_SIZE: 'fontSize',
  FONT_FAMILY: 'fontFamily',
  FONT_WEIGHT: 'fontWeight',
  FONT_STYLE: 'fontStyle',
  FILL: 'fill',
  STROKE: 'stroke',
  STROKE_WIDTH: 'strokeWidth',
  STROKE_DASH_ARRAY: 'strokeDashArray',
  STROKE_LINE_JOIN: 'strokeLineJoin',
  PAINT_FIRST: 'paintFirst',
  TEXT_ALIGN: 'textAlign',
  UNDERLINE: 'underline',
  LINETHROUGH: 'linethrough',
  CHAR_SPACING: 'charSpacing',
  LINE_HEIGHT: 'lineHeight',
  TEXT_MODE: 'textMode',
  AUTO_FIT_MAX_LINES: 'autoFitMaxLines',
  AUTO_FIT_MAX_FONT_SIZE: 'autoFitMaxFontSize',
  AUTO_WIDTH_MAX_WIDTH: 'autoWidthMaxWidth',
  FILL_GRADIENT: 'fillGradient',
  STROKE_GRADIENT: 'strokeGradient',
  TEXT_FORMAT: 'textFormat'
} as const;

export const DEFAULT_TEXT_COLOR = '#000000';

/** Text-layer properties that map 1:1 to Fabric object props */
export const TEXT_FABRIC_KEYS: readonly (keyof TextLayer)[] = [
  ...BASE_FABRIC_KEYS,
  TEXT_PROPS.WIDTH,
  TEXT_PROPS.FONT_SIZE,
  TEXT_PROPS.FONT_FAMILY,
  TEXT_PROPS.FONT_WEIGHT,
  TEXT_PROPS.FONT_STYLE,
  TEXT_PROPS.FILL,
  TEXT_PROPS.STROKE,
  TEXT_PROPS.STROKE_WIDTH,
  TEXT_PROPS.STROKE_LINE_JOIN,
  TEXT_PROPS.PAINT_FIRST,
  TEXT_PROPS.TEXT_ALIGN,
  TEXT_PROPS.UNDERLINE,
  TEXT_PROPS.LINETHROUGH,
  TEXT_PROPS.CHAR_SPACING,
  TEXT_PROPS.LINE_HEIGHT
];

/** Per-character style properties that Fabric stores in `styles`. */
export const PER_CHAR_STYLE_KEYS = [
  TEXT_PROPS.FONT_SIZE,
  TEXT_PROPS.FONT_FAMILY,
  TEXT_PROPS.FONT_WEIGHT,
  TEXT_PROPS.FONT_STYLE,
  TEXT_PROPS.UNDERLINE,
  TEXT_PROPS.LINETHROUGH,
  TEXT_PROPS.FILL,
  TEXT_PROPS.STROKE,
  TEXT_PROPS.STROKE_WIDTH
] as const;

export const DEFAULT_TEXT_STYLES: TextStylePreset[] = [
  {
    name: 'Heading',
    text: 'Heading',
    fontSize: 36,
    lineHeight: 1.33,
    fontFamily: 'Roboto',
    fontWeight: 700
  },
  {
    name: 'Sub Heading',
    text: 'Sub Heading',
    fontSize: 24,
    lineHeight: 1.5,
    fontFamily: 'Roboto',
    fontWeight: 500
  },
  {
    name: 'Body',
    text: 'Body',
    fontSize: 18,
    lineHeight: 1.43,
    fontFamily: 'Roboto',
    fontWeight: 400
  },
  {
    name: 'Caption',
    text: 'Caption',
    fontSize: 14,
    lineHeight: 1.33,
    fontFamily: 'Roboto',
    fontWeight: 400
  }
];

export const FONT_SIZES = [8, 9, 10, 12, 14, 16, 18, 24, 36, 48, 60] as const;

export const FONT_SIZE_REPEAT_INTERVAL_MS = 70;
export const FONT_SIZE_REPEAT_DELAY_MS = 400;

export const ALIGN_OPTIONS = [
  { value: 'left', icon: ICONS.textToolbar.alignLeft, labelKey: 'labels.left' },
  {
    value: 'center',
    icon: ICONS.textToolbar.alignCenter,
    labelKey: 'labels.center'
  },
  {
    value: 'right',
    icon: ICONS.textToolbar.alignRight,
    labelKey: 'labels.right'
  },
  {
    value: 'justify',
    icon: ICONS.textToolbar.alignJustify,
    labelKey: 'labels.justify'
  }
] as const;

export const TEXT_MODE_OPTIONS = [
  {
    value: TEXT_MODE.WRAP,
    icon: ICONS.textToolbar.textModeWrap,
    labelKey: 'labels.textMode.wrap'
  },
  {
    value: TEXT_MODE.AUTO_FIT,
    icon: ICONS.textToolbar.textModeAutoFit,
    labelKey: 'labels.textMode.autoFit'
  },
  {
    value: TEXT_MODE.AUTO_WIDTH,
    icon: ICONS.textToolbar.textModeAutoWidth,
    labelKey: 'labels.textMode.autoWidth'
  }
] as const;

export const TEXT_PANEL_ITEMS = [
  {
    id: 1,
    title: 'labels.defaultStyles',
    key: 'textStyles'
  }
];

// ── Text format types (stored on layer) ──────────────────────────────

// ── Static currency definitions (never stored on layer) ──────────────

export const CURRENCIES: CurrencyDef[] = [
  {
    code: 'INR',
    name: 'Indian Rupee',
    symbol: '₹',
    defaultSeparator: 'indian',
    defaultPlacement: 'prefix',
    defaultDisplayType: 'symbol'
  },
  {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    defaultSeparator: 'comma',
    defaultPlacement: 'prefix',
    defaultDisplayType: 'symbol'
  },
  {
    code: 'EUR',
    name: 'Euro',
    symbol: '€',
    defaultSeparator: 'dot',
    defaultPlacement: 'prefix',
    defaultDisplayType: 'symbol'
  },
  {
    code: 'GBP',
    name: 'British Pound',
    symbol: '£',
    defaultSeparator: 'comma',
    defaultPlacement: 'prefix',
    defaultDisplayType: 'symbol'
  },
  {
    code: 'JPY',
    name: 'Japanese Yen',
    symbol: '¥',
    defaultSeparator: 'comma',
    defaultPlacement: 'prefix',
    defaultDisplayType: 'symbol'
  },
  {
    code: 'CHF',
    name: 'Swiss Franc',
    symbol: 'Fr.',
    defaultSeparator: 'apostrophe',
    defaultPlacement: 'prefix',
    defaultDisplayType: 'code'
  }
];

export const SEPARATOR_OPTIONS: SeparatorDef[] = [
  {
    key: 'indian',
    label: 'Indian',
    example: '1,00,000',
    char: ',',
    grouping: 'indian'
  },
  {
    key: 'comma',
    label: 'Comma (US/UK)',
    example: '1,000,000',
    char: ',',
    grouping: 'standard'
  },
  {
    key: 'dot',
    label: 'Dot (European)',
    example: '1.000.000',
    char: '.',
    grouping: 'standard'
  },
  {
    key: 'apostrophe',
    label: 'Apostrophe (CH)',
    example: "1'000'000",
    char: "'",
    grouping: 'standard'
  },
  {
    key: 'space',
    label: 'Space (Nordic)',
    example: '1 000 000',
    char: ' ',
    grouping: 'standard'
  }
];

// O(1) lookups for formatter — derived from the arrays above
export const CURRENCY_MAP: Record<string, CurrencyDef> = Object.fromEntries(
  CURRENCIES.map(c => [c.code, c])
);

export const SEPARATOR_MAP: Record<SeparatorKey, SeparatorDef> =
  Object.fromEntries(SEPARATOR_OPTIONS.map(s => [s.key, s])) as Record<
    SeparatorKey,
    SeparatorDef
  >;
